import { SubPageShell, PageFaq, ProfessionalBadge } from "@/components/site/SubPage";
import { useSeo } from "@/lib/useSeo";
import { bookCall, openSopralluogo } from "@/lib/site";

const callLuciano = () => bookCall("Chiamata con Luciano — aprire o ripensare una struttura", "aprire-bb");
const apriModulo = () => openSopralluogo("trasformare");

const FAQ = [
  {
    q: "Quanto costa aprire una struttura ricettiva?",
    a: "Dipende dai lavori necessari, dal comune e dal tipo di attività. Proprio per questo il primo passo è la fattibilità: numeri veri su costi e ricavi attesi, prima di spendere.",
  },
  {
    q: "Quanto tempo serve per aprire?",
    a: "Dipende da pratiche edilizie e iter comunali, che cambiano da territorio a territorio. Con la fattibilità iniziale ti diamo una stima realistica per il tuo caso, non una risposta generica.",
  },
  {
    q: "Serve cambiare la destinazione d'uso dell'immobile?",
    a: "Dipende dal comune e dal tipo di struttura che vuoi aprire: non è sempre necessario. Lo verifica Massimiliano già nella fase di fattibilità, prima dei lavori.",
  },
  {
    q: "Il sopralluogo si paga?",
    a: "Le condizioni te le dice Massimiliano prima di fissarlo, per iscritto. Se poi si prosegue con il progetto, il sopralluogo viene scalato dal preventivo.",
  },
  {
    q: "Posso aprire anche se non abito in Sicilia?",
    a: "Sì. Il nostro lavoro è seguire tutto sul territorio: progetto, pratiche, foto e lancio online. Tu segui ogni passaggio da remoto, con una sola persona di riferimento.",
  },
];

const FASI = [
  {
    n: "01",
    t: "Fattibilità, con numeri veri",
    d: "I numeri li fa Luciano: quanto può rendere la tua struttura, in quella zona, con quei metri quadri, prima di spendere un euro. Se non tornano, te lo dice subito — meglio saperlo prima.",
  },
  {
    n: "02",
    t: "Il progetto degli spazi",
    d: "Massimiliano Masellis, architetto fondatore dello studio MMA+ di Palermo, progetta spazi pensati per ospitare e vendere, non solo per piacere. Lo studio lavora dal 2017 su progetti residenziali, ricettivi e di ristorazione, in Italia e nel mondo: la tua struttura nasce già pronta a farsi fotografare e prenotare.",
  },
  {
    n: "03",
    t: "Pratiche e burocrazia",
    d: "Pratiche edilizie, pratica ricettiva, partita IVA, SCIA, CIN: Valerio, il nostro commercialista specializzato in strutture ricettive, segue la parte fiscale e amministrativa. Tu non vedi la burocrazia: vedi solo che è fatta.",
  },
  {
    n: "04",
    t: "Le foto che vendono",
    d: "Giovanni di Archifotografia, fotografo di interni per strutture ricettive con oltre mille alloggi fotografati, presenta la tua struttura come merita: è la prima cosa che l'ospite guarda.",
  },
  {
    n: "05",
    t: "Lancio online e prezzi",
    d: "Annunci, portali, calendario e strategia prezzi dal primo giorno: la struttura apre già impostata per lavorare, non per improvvisare. E da lì in poi la seguiamo ogni giorno, se vuoi.",
  },
];

const PROGETTI = [
  { src: "/images/masellis/progetto-2.webp", alt: "Living di una casa progettata dallo studio MMA+ Architetti: pareti in legno, marmo e luce naturale" },
  { src: "/images/masellis/progetto-1.webp", alt: "Interno residenziale MMA+ con specchio e cortile-giardino" },
  { src: "/images/masellis/progetto-4.webp", alt: "Soggiorno con parete curva in legno, progetto residenziale di Massimiliano Masellis" },
  { src: "/images/masellis/progetto-3.webp", alt: "Parete materica e terrazza di un'abitazione progettata da MMA+" },
];

const LIVELLI = [
  { n: "1", t: "Contatto", who: "Hai un immobile o un'idea", how: "Raccontaci il tuo spazio nel modulo: Massimiliano legge i dati e ti richiama entro 48 ore lavorative", price: "Gratis", cta: "Raccontaci il tuo spazio", action: apriModulo, testid: "aprire-livello-1-cta" },
  { n: "2", t: "Sopralluogo di progetto", who: "Vuoi capire cosa si può fare", how: "Massimiliano vede lo spazio di persona e ti dice cosa ci farebbe", price: "Condizioni chiare prima · scalato dal progetto" },
  { n: "3", t: "Progetto con MMA+", who: "Hai deciso", how: "Progetto completo della tua struttura, dal sopralluogo in avanti", price: "Preventivo su misura, per iscritto" },
];

const SCELTE = [
  { id: "immobile", t: "Ho un immobile da trasformare", cta: "Sopralluogo di progetto", action: apriModulo },
  { id: "rinnovo", t: "Ho già una struttura da rinnovare", cta: "Valutazione degli spazi", action: () => openSopralluogo("rinnovare") },
  { id: "capire", t: "Voglio solo capire se conviene", cta: "30 minuti con Luciano", action: callLuciano },
];

const btnPrimary =
  "btn-glow inline-flex min-h-[44px] items-center justify-center rounded-full bg-[#E05A2C] px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98]";
const btnGhost =
  "inline-flex min-h-[44px] items-center justify-center rounded-full border border-[#383029] px-5 py-2.5 text-sm text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C] hover:text-[#E05A2C]";
const h2 = "mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]";

function CtaStrip({ testid, title, sub, label, onClick }) {
  return (
    <div data-testid={testid} className="mt-8 border-l-2 border-[#E05A2C] bg-[#16110E] p-6 sm:p-7">
      <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">{title}</p>
      {sub && <p className="mt-2 text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">{sub}</p>}
      <button onClick={onClick} data-testid={`${testid}-button`} className={`${btnPrimary} mt-5`}>
        {label}
      </button>
    </div>
  );
}

export default function AprireBbPage() {
  useSeo({
    title: "Aprire o ripensare una struttura ricettiva in Sicilia | YU YieldUp",
    description:
      "Aprire una struttura ricettiva in Sicilia o ripensare gli spazi di una già attiva: fattibilità con numeri veri, progetto con architetto, pratiche, foto professionali e lancio online. B&B, case vacanze, ville, hotel.",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: "Apertura di strutture ricettive e case vacanze in Sicilia: fattibilità, progetto, burocrazia e lancio",
          provider: { "@type": "Organization", name: "YU YieldUp" },
          areaServed: { "@type": "AdministrativeArea", name: "Sicilia" },
          description:
            "Percorso completo per aprire un'attività ricettiva in Sicilia: fattibilità economica, progettazione degli spazi, pratiche edilizie e fiscali, foto professionali e lancio online.",
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      ],
    },
  });

  return (
    <SubPageShell callSource="aprire-bb">
      <section data-testid="aprire-bb-page" className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
        <p className="section-kicker">Per chi parte da zero — e per chi vuole ripensare gli spazi</p>
        <h1 className="mt-4 font-heading text-3xl font-black uppercase leading-[1.02] tracking-tighter text-[#FAF7F2] sm:text-5xl">
          Apri o ripensa la tua struttura ricettiva in Sicilia
        </h1>
        <p className="mt-6 text-base font-light leading-relaxed text-[#C4BBB0] sm:text-lg">
          B&B, casa vacanze, villa, affittacamere, hotel: hai un immobile, un'idea, i lavori già iniziati — o una struttura aperta con spazi interni ed esterni da ripensare. Quello che manca quasi mai è qualcuno che tenga insieme tutto: i numeri prima, il progetto, le pratiche, il lancio. Con YU YieldUp hai un solo interlocutore dall'inizio alla fine.
        </p>
        <ProfessionalBadge testid="aprire-badge" photo="/images/massimiliano.jpg" name="Massimiliano Masellis, architetto" caption="Studio MMA+, Palermo. Quello che progetta i tuoi spazi." />

        <div data-testid="aprire-hero-cta" className="mt-8">
          <button onClick={apriModulo} data-testid="aprire-cta-sopralluogo" className={btnPrimary}>
            Richiedi un sopralluogo di progetto
          </button>
          <p className="mt-3 text-sm font-light leading-relaxed text-[#C4BBB0]">
            Spazi pensati per gli ospiti: comfort, recensioni e valore che durano nel tempo.
          </p>
          <button
            data-testid="aprire-cta-15min"
            onClick={callLuciano}
            className="mt-3 min-h-[44px] text-sm text-[#A89F91] underline decoration-[#383029] underline-offset-4 transition-colors duration-300 hover:text-[#E05A2C]"
          >
            Prima vuoi capire se conviene? 30 minuti gratis con Luciano
          </button>
        </div>

        <h2 className={h2}>Come si comincia</h2>
        <div className="mt-6 grid gap-px bg-[#383029] sm:grid-cols-3">
          {LIVELLI.map((s) => (
            <div key={s.n} data-testid={`aprire-livello-${s.n}`} className="flex flex-col bg-[#16110E] p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-[#E05A2C]">Livello {s.n}</p>
              <p className="mt-2 font-heading text-base font-black uppercase tracking-tight text-[#FAF7F2]">{s.t}</p>
              <p className="mt-2 text-sm font-light text-[#A89F91]">{s.who}</p>
              <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-[#C4BBB0]">{s.how}</p>
              <p className="mt-4 border-t border-[#383029] pt-3 font-mono text-xs uppercase tracking-widest text-[#FAF7F2]">{s.price}</p>
              {s.cta && (
                <button onClick={s.action} data-testid={s.testid} className={`${btnGhost} mt-4`}>
                  {s.cta}
                </button>
              )}
            </div>
          ))}
        </div>

        <h2 className={h2}>Le cinque fasi</h2>
        <div className="mt-8 space-y-8">
          {FASI.map((fase) => (
            <div key={fase.n}>
              <div data-testid={`fase-${fase.n}`} className="border-t border-[#2A241F] pt-6">
                <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">
                  <span className="mr-3 text-[#E05A2C]">{fase.n}</span>
                  {fase.t}
                </p>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">{fase.d}</p>
              </div>
              {fase.n === "02" && (
                <CtaStrip
                  testid="aprire-cta-dopo-progetto"
                  title="Parlaci del tuo progetto"
                  sub="Un ospite ricorda come si è sentito in una stanza. Lì nascono le recensioni. Raccontaci lo spazio: Massimiliano ti richiama lui."
                  label="Raccontaci il tuo spazio"
                  onClick={apriModulo}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-12">
          <p className="section-kicker">Alcuni progetti dello studio MMA+</p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            {PROGETTI.map((p, i) => (
              <div key={p.src} data-testid={`masellis-photo-${i + 1}`} className="overflow-hidden border border-[#2A241F]">
                <img src={p.src} alt={p.alt} loading="lazy" className="aspect-[4/3] h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]" />
                {p.caption && <p className="border-t border-[#2A241F] px-3 py-2 font-mono text-xs uppercase tracking-widest text-[#A89F91]">{p.caption}</p>}
              </div>
            ))}
          </div>
          <p className="mt-3 font-mono text-xs uppercase tracking-widest text-[#A89F91]">
            Progetti: Massimiliano Masellis · MMA+ Architetti, Palermo
          </p>
          <CtaStrip testid="aprire-cta-dopo-galleria" title="Vuoi spazi così per la tua struttura?" label="Richiedi un sopralluogo di progetto" onClick={apriModulo} />
        </div>

        <div data-testid="aprire-gia-aperta" className="mt-14 border border-[#4D2C20] bg-[#2B1B15] p-6 sm:p-8">
          <p className="section-kicker">Per chi è già aperto</p>
          <p className="mt-3 font-heading text-xl font-black uppercase tracking-tight text-[#FAF7F2]">La tua struttura è già aperta?</p>
          <p className="mt-3 text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">
            Se le recensioni parlano di spazi stretti, arredi datati o poca luce, il problema non si risolve col prezzo. Si risolve con il progetto.
          </p>
          <button onClick={() => openSopralluogo("rinnovare")} data-testid="aprire-cta-valuta-spazi" className={`${btnPrimary} mt-6`}>
            Fai valutare i tuoi spazi
          </button>
        </div>

        <h2 className={h2}>Domande frequenti</h2>
        <PageFaq items={FAQ} />

        <div data-testid="aprire-tre-scelte" className="mt-14 border-t border-[#2A241F] pt-10">
          <p className="section-kicker">Da dove parti?</p>
          <div className="mt-6 space-y-3">
            {SCELTE.map((s) => (
              <button
                key={s.id}
                onClick={s.action}
                data-testid={`aprire-scelta-${s.id}`}
                className="group flex min-h-[44px] w-full flex-wrap items-center justify-between gap-3 border border-[#2A241F] px-5 py-4 text-left transition-colors duration-300 hover:border-[#E05A2C]"
              >
                <span className="flex items-center gap-3 text-base text-[#FAF7F2]">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#E05A2C]" />
                  {s.t}
                </span>
                <span className="font-mono text-xs uppercase tracking-widest text-[#E05A2C] group-hover:underline">→ {s.cta}</span>
              </button>
            ))}
          </div>
          <div className="mt-8 space-y-2">
            {[
              "Progettiamo per chi ci dormirà, non per una foto.",
              "Un buon progetto si ripaga nelle recensioni degli anni successivi.",
              "Prima vedi cosa si può fare, poi decidi. Nessun preventivo a sorpresa.",
            ].map((line) => (
              <p key={line} className="text-sm font-light leading-relaxed text-[#A89F91]">
                {line}
              </p>
            ))}
          </div>
          <a href="/" data-testid="aprire-back-home" className={`${btnGhost} mt-8`}>
            Scopri tutto il lavoro di YU YieldUp
          </a>
        </div>
      </section>
    </SubPageShell>
  );
}
